import { useMemo } from 'react';
import type { AdminIssue } from 'api/adminIssuesApi';
import { useMissionAssignments } from 'hooks/useInterventionRequests';
import { mergeAssignments, type UpcomingMission } from './components/upcomingAssignment';

/**
 * The Jira listing on screen, joined to what Postgres knows about each mission's assignment.
 *
 * Written once because **both** tabs need it: « À qualifier » to render the missions, « Tous les
 * tickets » to decide whether a scheduled ticket may be solicited again. Two copies would be two
 * copies of `assignmentsKnown`, which is the subtle half — and the day one was fixed the other
 * would drift.
 */

/** `enabled: false` asks for nothing at all, so a screen that cannot use the join does not fetch it. */
export const useMissions = (
  serviceDeskId: number,
  issues: AdminIssue[],
  { enabled = true }: { enabled?: boolean } = {}
): { missions: UpcomingMission[]; assignmentsKnown: boolean; error: Error | null } => {
  const issueKeys = useMemo(
    () => (enabled ? issues.map((issue) => issue.issueKey) : []),
    [enabled, issues]
  );
  const assignments = useMissionAssignments(serviceDeskId, issueKeys);
  const missions = useMemo(
    () => mergeAssignments(issues, assignments.data),
    [issues, assignments.data]
  );

  return {
    missions,
    // `isPlaceholderData` as well as `isSuccess`: `keepPreviousData` hands back the **previous**
    // page's assignments while the next one loads, and those miss every key on screen — which
    // reads as « Sans technicien » just as loudly as a failure does.
    assignmentsKnown: assignments.isSuccess && !assignments.isPlaceholderData,
    error: (assignments.error as Error) ?? null,
  };
};
